import type { Job } from '@/types'

export const CALENDAR_SLOT_MINUTES = 30
export const MIN_JOB_DURATION_MINUTES = 30
export const DEFAULT_DROP_DURATION_MINUTES = 120

export const DEFAULT_DAY_START_HOUR = 6
export const DAY_END_HOUR = 24
export const HOUR_ROW_PX = 64
export const API_DAY_END_MINUTES = DAY_END_HOUR * 60 - 1

export interface ResizePreviewRange {
  startMinutes: number
  endMinutes: number
}

export function timeStringToMinutes(value?: string | null): number | null {
  if (!value) return null

  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value)
  if (!match) return null

  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours < 0 || hours > DAY_END_HOUR || minutes < 0 || minutes > 59) {
    return null
  }
  if (hours === DAY_END_HOUR && minutes !== 0) return null

  return hours * 60 + minutes
}

export function minutesToTimeString(minutes: number): string {
  const roundedMinutes = clampMinutes(
    Math.floor(minutes),
    0,
    API_DAY_END_MINUTES
  )
  const hours = Math.floor(roundedMinutes / 60)
  const mins = roundedMinutes % 60

  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

export function snapMinutes(
  minutes: number,
  step = CALENDAR_SLOT_MINUTES
): number {
  const safeStep = step > 0 ? step : CALENDAR_SLOT_MINUTES
  return Math.round(minutes / safeStep) * safeStep
}

export function clampMinutes(
  minutes: number,
  min: number,
  max: number
): number {
  return Math.min(max, Math.max(min, minutes))
}

export function getJobDurationMinutes(job: Job): number {
  const start = timeStringToMinutes(job.start_time)
  const end = timeStringToMinutes(job.end_time)

  if (start == null || end == null || end <= start) {
    return DEFAULT_DROP_DURATION_MINUTES
  }

  return Math.max(MIN_JOB_DURATION_MINUTES, end - start)
}

export function resizePreviewMatchesTimeslot(
  preview: ResizePreviewRange,
  startTime: string | null | undefined,
  endTime: string | null | undefined
): boolean {
  const start = timeStringToMinutes(startTime)
  const end = timeStringToMinutes(endTime)

  return start === preview.startMinutes && end === preview.endMinutes
}

export function computeDropTimeRange({
  pointerMinutes,
  dayStartHour,
  durationMinutes,
}: {
  pointerMinutes: number
  dayStartHour: number
  durationMinutes: number
}): { start: number; end: number } {
  const gridStartMinutes = dayStartHour * 60
  const dayEndMinutes = API_DAY_END_MINUTES
  const requestedDuration =
    durationMinutes > 0 ? durationMinutes : DEFAULT_DROP_DURATION_MINUTES
  const safeDuration = Math.max(MIN_JOB_DURATION_MINUTES, requestedDuration)
  const visibleDuration = Math.min(
    safeDuration,
    dayEndMinutes - gridStartMinutes
  )
  const latestStartMinutes = Math.max(
    gridStartMinutes,
    Math.floor((dayEndMinutes - visibleDuration) / CALENDAR_SLOT_MINUTES) *
      CALENDAR_SLOT_MINUTES
  )
  const start = clampMinutes(
    snapMinutes(pointerMinutes),
    gridStartMinutes,
    latestStartMinutes
  )
  const end = clampMinutes(
    start + visibleDuration,
    start + MIN_JOB_DURATION_MINUTES,
    dayEndMinutes
  )

  return { start, end }
}

export function yToMinutes(
  y: number,
  gridTop: number,
  dayStartHour: number
): number {
  const gridStartMinutes = dayStartHour * 60
  const minutes = dayStartHour * 60 + ((y - gridTop) / HOUR_ROW_PX) * 60
  return clampMinutes(Math.floor(minutes), gridStartMinutes, DAY_END_HOUR * 60)
}

export function minutesToY(minutes: number, dayStartHour: number): number {
  return ((minutes - dayStartHour * 60) / 60) * HOUR_ROW_PX
}
